import React, { useEffect, useState } from "react";
import LavorisService from "../../services/LavorisService";
import { TableActions } from "../../components/others/TableActions";
import { useModal } from "../../hooks";
import { DetailTurnWithDate } from "../others/detailsTurn";
import moment from 'moment-timezone';
import { filterMoldeExamPractice } from "../../utils/filterMoldeExamPractice";
import { useSelector } from "react-redux";
import { RootState } from "../../store/combineReducer";
import { useAlert } from "../../hooks/useAlert";
import { extractExamFromMetadata, extractPracticesFromMetadata } from "../../utils/metadata";
import { handleOpenPdfReport, handleOpenPdfReportPractice } from "../../utils/files";
import MultiplePracticesModal from "./MultiplePracticesModal";
import { employeeAbsences } from "../Absences/Types/Absences.type";
import AbsenteeismResult from "../others/absenteeismResult";
import InfoOffcanvas from '../../components/others/InfoOffCanvas/InfoOffCanvas';
import InfiniteTable from "../../components/others/InfiniteTableComponent/IntiniteTableComponent";
import exelReports from "./exelReports";
import { formatDateTimeForTable } from "../../utils/formatDate";
import "./Reports.scss";

const VIEW_APPOINTMENT_MODAL = "viewAppointmentModal";
const VIEW_MULTIPLE_PRACTICES_MODAL = 'viewMultiplePracticesModal'

const Reports = () => {
    const controller = new AbortController();
    const signal = controller.signal;
    const today = new Date();
    const formattedToday = today.toISOString().split("T")[0]; // YYYY-MM-DD
    const [from, setFrom] = useState<string>(formattedToday);
    const [to, setTo] = useState<string>(formattedToday);
    const [fromFormatted, setFromFormatted] = useState<string>(moment().format('DD/MM/YYYY'));
    const [toFormatted, setToFormatted] = useState<string>(moment().format('DD/MM/YYYY'));
    const [hasMore, setHasMore] = useState(true);
    const [searchQuery, setSearcQuery] = useState<string | null>(null);
    const hasSearchQuery = searchQuery && searchQuery.length > 0
    const LavorisInstance = LavorisService.getInstance();
    const currentCompany = useSelector((state: RootState) => state.user.currentCompany || state.user.companies[0].id);
    const { closeModal, openModal, isActive } = useModal();
    const { showAlert } = useAlert();
    const [items, setItems] = useState({
        itemsPerPage: 20,
        page: 1,
    });
    const [selectedEmployee, setSelectedEmployee] = useState<employeeAbsences>();
    const [data, setData] = useState<any[]>([]);
    const [loader, setLoader] = useState(false);
    const [rowData, setRowData] = useState<any>()
    const [appId, setAppId] = useState<string>("")
    const status = ['Completada', 'Finalizado'];
    //const status = ['Completada', 'Aptitud parcialmente completada', 'Eliminado', 'Finalizado'];

    useEffect(() => {
        if (items.page === 1) {
            setData([]);
        }
        return () => {
            controller.abort();
        };
    }, [items.page]);

    const getAllAppointmentsCall = async () => {
        setLoader(true);
        try {
            const app = await LavorisInstance.TurnServices.getAppointmentsWithSearchQueryByCompaniesId(currentCompany, items.itemsPerPage, items.page, fromFormatted, toFormatted, signal, hasSearchQuery ? searchQuery : "", status)

            if (app.items.length === 0) {
                setHasMore(false);
                return;
            }
            const existingIds = new Set(data?.map(item => item.id));
            const uniqueAppointments = app?.items?.filter(appointment => !existingIds.has(appointment.id));

            setData(prevData => [...prevData, ...uniqueAppointments]);
            setItems(prev => ({ ...prev, page: prev.page + 1 }));
        } catch (e) {
            console.log('Error en getAllAppointmentsCall:', e);
            setHasMore(false);

        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        getAllAppointmentsCall();
        return () => {
            controller.abort()
        };
    }, [])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const querySearch = e.target.value;
        setSearcQuery(querySearch);
    }

    const searchWithDatesOnly = async () => {
        setLoader(true);
        try {
            setItems(prev => ({ ...prev, page: 1 }));
            let app = await LavorisInstance.TurnServices.getAppointmentsWithSearchQueryByCompaniesId(currentCompany, items.itemsPerPage, 1, fromFormatted, toFormatted, signal, "", status)

            if (app.items.length === 0) {
                showAlert("No se encontraron registros");
                return
            } else {
                setData(app.items);
                setItems(prev => ({ ...prev, page: prev.page + 1 }));
                setHasMore(true);
            }

        } catch (error) {
            console.log(error);
            setHasMore(false);
        } finally {
            setLoader(false);
        }
    }

    const searchWitQuery = async () => {
        setLoader(true);
        try {
            let app = await LavorisInstance.TurnServices.getAppointmentsWithSearchQueryByCompaniesId(currentCompany, items.itemsPerPage, 1, fromFormatted, toFormatted, signal, searchQuery, status)

            if (app.items.length === 0) {
                showAlert("No se encontraron registros");
                return
            } else {
                setData(app.items);
                setItems(prev => ({ ...prev, page: prev.page + 1 }));
                setHasMore(true);
            }

        } catch (error) {
            console.log(error);
            setHasMore(false);
        } finally {
            setLoader(false);
        }
    }

    const onSearch = async () => {
        try {
            if (hasSearchQuery) {
                await searchWitQuery()
            } else {
                await searchWithDatesOnly()
            }

        } catch (error) {
            console.log(error);
        }
    };

    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateValue = e.target.value;
        setFrom(dateValue); // Mantiene el valor en formato 'YYYY-MM-DD' para el input
        setFromFormatted(moment(dateValue).format('DD/MM/YYYY')); // Guarda en formato 'DD/MM/YYYY' para procesarlo
    };

    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const dateValue = e.target.value;
        setTo(dateValue); // Mantiene el valor en formato 'YYYY-MM-DD' para el input
        setToFormatted(moment(dateValue).format('DD/MM/YYYY')); // Guarda en formato 'DD/MM/YYYY' para procesarlo
    };

    data.sort((a, b) => {
        const createdDateA = new Date(a.createdDate);
        const createdDateB = new Date(b.createdDate);
        // Ordenar en orden descendente (de lo más reciente a lo más antiguo)
        return createdDateB.getTime() - createdDateA.getTime();
    });

    const returnDateTime = (rowData: any) => {
        if (rowData?.type === 'Visita a Domicilio' || rowData?.type === 'Videollamada') return "---";
        if (rowData?.startTime) return formatDateTimeForTable(rowData?.date, rowData?.startTime);
        return "---";
    }

    const returnDescription = (rowData: any) => {
        if (rowData.type === 'Videollamada') return 'Telecontrol'
        if (rowData.type === 'Visita a Domicilio') return 'Control en Domicilio'
        if (rowData.type === 'Consulta en Lavoris') return 'Control en Consultorio Lavoris'
        if (rowData.type === 'Define Lavoris') return 'AI - Ausentismo Inteligente'
        return filterMoldeExamPractice(rowData)
    }

    // const returnAdmissionDate = (rowData: any) => {
    //     if (rowData?.report?.visitManualTime) return rowData?.report?.visitManualTime;
    //     if (rowData?.is_prestador && rowData?.status === 'Finalizado') return rowData?.admissionTime;
    //     if (!rowData?.actions || rowData?.actions?.length === 0) return rowData?.admissionTime || "---";
    //     if (!rowData?.admissionTime) return '---';
    //     const actions = ['ADMITIDO', 'ADMITIDO/TOTEM']
    //     const admitido = rowData?.actions.filter((action: any) => actions.includes(action.action));
    //     if (!rowData?.is_prestador && admitido.length === 0) return rowData?.admissionTime || "---";
    //     return moment(admitido[0].date).format('DD/MM/YY HH:mm');
    // }
    const returnAdmissionDate = (rowData: any) => {
        if (rowData?.admissionTime) return rowData?.admissionTime;
        if (rowData?.report?.visitManualTime) return rowData?.report?.visitManualTime;
        return "---";
    }

    const returnCreation = (rowData: any) => {
        if (!rowData?.actions || rowData?.actions?.length === 0) return moment(rowData.createdDate).format('DD/MM/YYYY HH:mm') || '---';
        const actions = ['SOLICITADO', 'CREADO', 'CREACION/REPROGRAMACION']
        const action = rowData?.actions.filter((action: any) => actions.includes(action.action));
        if (action.length === 0) return moment(rowData.createdDate).format('DD/MM/YY HH:mm') || '---';
        return moment(action[0].date).format('DD/MM/YY HH:mm');
    }

    const returnResults = (rowData: any) => {
        if (rowData?.type === 'Visita a Domicilio' || rowData?.type === 'Videollamada') {
            const report = rowData?.report
            const diagnostico = report?.diagnostic
            return diagnostico || "---";
        }

        const hasOnlyIsolatedPractices = (row: any) => {
            if (row.metadata === undefined) return false;
            const practices = extractPracticesFromMetadata(row.metadata);
            if (!practices) return false;
            return practices.every((p: any) => p.type === 'PRACTICE' || p.type === 'PRACTICE_IN_MOLDE');
        };

        const findExam = (appointment: any) => {
            if (!appointment?.metadata) return;
            return extractExamFromMetadata(appointment?.metadata);
        };

        const findConclusion = (exam: any): string => {
            if (!exam) return "";
            const conclusion =
                exam?.form?.find((field: any) => field.type === "conclusiones" || field.type === "select")?.response
                    ?.input_value || "";
            return conclusion.replaceAll("_", " ").toUpperCase();
        };

        const findConclusionComents = (exam: any): string => {
            if (!exam) return "";
            const conclusionComments =
                exam?.form?.find((field: any) => field.type === "textarea")?.response
                    ?.input_value || null;
            return conclusionComments || "";
        }

        const insolatePracticeResult = (rowData: any) => {
            const practices = extractPracticesFromMetadata(rowData.metadata)
            const practicesForm = practices?.map((p: any) => p?.form)
            const result = practicesForm?.map((form: any) => {
                const controlEnConsultorio = form?.find((element: any) => element?.label === 'Control en Consultorio');
                if (controlEnConsultorio) {
                    const diagnostico = form?.find((element: any) => element?.label === 'Diagnósticos');
                    if (diagnostico) {
                        return diagnostico?.response?.input_value?.label;
                    }
                }

                const conclusion = form?.filter((element: any) => element?.label === 'Conclusión' && element?.response)[0];
                if (conclusion) return conclusion?.response?.input_value;

                const icTraumatologica = form?.find((element: any) => element?.label === 'Interconsulta Traumatológica');
                if (icTraumatologica) {
                    const informe = form?.filter((element: any) => element?.label === 'Informe' && element?.response)[0];
                    return informe?.response?.input_value;
                }

                const observation = form?.filter((element: any) => element?.label === 'Observacion' && element?.response)[0];
                if (observation) return observation?.response?.input_value + ",";
                return null;
            });
            return result?.filter((r: any) => r !== null).join(", ") || "---";
        }

        if (hasOnlyIsolatedPractices(rowData)) {
            return insolatePracticeResult(rowData);
        }

        const exam = findExam(rowData);
        if (!exam) return "---";

        const conclusion = findConclusion(exam)
            .split("_")
            .join(" ")
            .toLocaleLowerCase();

        const conclusionComments = findConclusionComents(exam);
        const fullConclusion = conclusionComments ? `${conclusion}: ${conclusionComments}` : conclusion;
        return fullConclusion || "---";
    }

    const giaSolicitud = (rowData: any) => {
        if (!rowData) return '---'
        const gia = rowData?.gia ? 'Si' : 'No'
        return (
            <p className={`${rowData.gia ? 'text-green-700' : 'text-red-700'} font-extrabold`}>{gia} </p>
        )
    }

    const get_constrol_consultorio_parte_medico = async (id: string) => {
        try {
            const getUrlId = await LavorisInstance.TurnServices.getAppPdfFile(id);
            if (getUrlId.length === 0) {
                showAlert('No hay PDF creado, comuniquese con Lavoris');
                return;
            }
            await LavorisInstance.FilesService.getReportAWSFileById(getUrlId);

        } catch (err) {
            console.log(err);
        }
    }

    const actions = (rowData: any) => {
        const hasOnlyIsolatedPractices = (row: any) => {
            if (rowData.metadata === undefined) return false;
            if (row.status !== 'Finalizado') return false;
            const practices = extractPracticesFromMetadata(row.metadata);
            if (!practices) return false;

            return practices.every((p: any) => p.type === "PRACTICE" || p.type === 'PRACTICE_IN_MOLDE');
        };
        const { simplified } = rowData?.metadata || {};
        const isAccidenteLaboral = simplified?.some((p: Practice) => p?.managerId === '2023')
        const isControlConsultorio = simplified?.some((p: Practice) => p?.managerId === '1026');
        const showWSIcon = isControlConsultorio || isAccidenteLaboral;
        const downloadSequentiallyPDFs = async (metadata: any) => {
            const practices = extractPracticesFromMetadata(metadata);
            const patient = metadata.patient.name
            if (!practices) return false;

            for (const p of practices) {
                await handleOpenPdfReportPractice((p as any).pdfUrl, p.name, patient);
            }
        };

        const findExam = (appointment: any) => {
            if (!appointment?.metadata) return;
            return extractExamFromMetadata(appointment?.metadata);
        };

        const hasExamFullReport = (row: any) => !!findExam(row)?.fullReportUrl;

        const getExamPDF = (metadata: any) => {
            const exam = extractExamFromMetadata(metadata);
            if (!exam?.fullReportUrl) throw new Error("No PDF generated for exam");

            return handleOpenPdfReport(exam.fullReportUrl);
        };

        if (rowData?.type === 'Visita a Domicilio' || rowData?.type === 'Videollamada') return (
            <div className="flex flex-wrap justify-center align-middle gap-2">
                <TableActions
                    type="View"
                    onClick={() => {
                        setSelectedEmployee(rowData);
                        openModal("modalReporteAusentismo", rowData.id);
                    }}
                />
                <TableActions
                    type="PDF"
                    title={'Descargar pdf'}
                    onClick={async () => {
                        await LavorisInstance.FilesService.getReportAWSFileById(
                            rowData?.reportFileIdPatient,
                        );
                    }}
                />

                {rowData.status === "Completada" && (
                    <>
                        <TableActions
                            type="Whatsapp"
                            title={!rowData.employee_ws_sent ? 'Parte médico no enviado' : 'Parte médico enviado'}
                            color={!rowData.employee_ws_sent ? 'red' : 'green'}
                            onClick={() => { }}
                        />

                        <TableActions
                            type="Download"
                            title="Descargar parte médico Empleado"
                            onClick={async () => {
                                await LavorisInstance.FilesService.getReportAWSFileById(
                                    rowData?.reportFileIdEmployee,
                                );
                            }}
                        />
                    </>
                )}
            </div>
        );

        if (rowData.status === 'Aptitud parcialmente completada') return 'Informe Pendiente';

        return (
            <div className="flex flex-wrap items-center justify-center gap-2">
                <TableActions
                    type="View"
                    onClick={() =>
                        openModal(VIEW_APPOINTMENT_MODAL, rowData.id)
                    }
                />
                {rowData?.status === 'Ausente' || rowData?.status === 'Eliminado' || rowData?.status === 'Cancelado' ? (
                    <TableActions
                        type="Time"
                        title={rowData?.status === 'Ausente' ? 'No se presentó' : '---'}
                        color={'blue'}
                        onClick={() => { }}
                    />
                ) : (
                    <TableActions
                        type="Time"
                        title={rowData?.tardiness || ''}
                        color={rowData?.is_late ? 'red' : 'green'}
                        onClick={() => { }}
                    />
                )}
                {hasExamFullReport(rowData) && (
                    <TableActions
                        type="PDF"
                        title={'Descargar Informe'}
                        onClick={() => getExamPDF(rowData.metadata)}
                    />
                )}
                {hasOnlyIsolatedPractices(rowData) && (
                    <>
                        <TableActions
                            type="Multiple PDF"
                            title="Ver PDFs indivualmente"
                            onClick={() => {
                                setRowData(rowData)
                                setAppId(rowData.id)
                                openModal(VIEW_MULTIPLE_PRACTICES_MODAL)
                            }}
                        />
                        <TableActions
                            type="Download"
                            title="Descargar PDF múltiples"
                            onClick={() => downloadSequentiallyPDFs(rowData.metadata)}
                        />
                    </>
                )}
                {showWSIcon && (<div className="w-auto m-[auto] mb-2">
                    <TableActions
                        type="Whatsapp"
                        title={!rowData.employee_ws_sent ? 'Parte médico no enviado' : 'Parte médico enviado'}
                        color={!rowData.employee_ws_sent ? 'red' : 'green'}
                        onClick={() => { }}
                    />
                </div>)}
            </div>
        );


    }

    return (
        <div className="reports-container">
            <div className="custom-header mt-4 pt-2">
                <div className="header-controls">
                    {/* Search Section */}
                    <div className="search-section">
                        <div className="search-input-group">
                            <div className="search-input-label-top">
                                <label htmlFor="search-dni">Buscar por DNI o Apellido</label>
                                <input
                                    title="Ingresar dni o apellido"
                                    type="search"
                                    placeholder="Ingresa un DNI o Apellido"
                                    className="search-input"
                                    onChange={handleSearchChange}
                                />
                            </div>
                            <div className="search-input-label-top">
                                <label htmlFor="search-date-from">Desde</label>
                                <input
                                    title="Desde"
                                    type="date"
                                    value={from ?? ""}
                                    placeholder="Desde"
                                    className="search-input"
                                    onChange={handleFromDateChange}
                                />
                            </div>
                            <div className="search-input-label-top">
                                <label htmlFor="search-date-to">Hasta</label>
                                <input
                                    title="Hasta"
                                    type="date"
                                    value={to ?? ""}
                                    placeholder="Hasta"
                                    className="search-input"
                                    onChange={handleToDateChange}
                                />
                            </div>
                            <button
                                type="button"
                                className="search-btn"
                                onClick={() => {
                                    onSearch()
                                }}
                                title="Buscar"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Actions Section */}
                    <div className="actions-section">
                        <InfoOffcanvas title="INFORMACIÓN DE LA TABLA">
                            <ul className="text-md p-auto m-auto">
                                <li>- La primera búsqueda trae resultados del día.</li>
                                <li>- Trae sólo los estados: Aptitud parcialmente completada, Finalizado, Completado.</li>
                                <li>- Se puede búscar solo con fechas.</li>
                                <li>- Si se búsca con apellido, nombre o dni y no se filtra por fecha, trae todo lo del día.</li>
                                <li>- Las fechas del filtro se pueden poner manualmente.</li>
                                <li>- A medida que se va haciendo scroll para abajo, la tabla va buscando más resultados.</li>
                                <li>- El excel descarga y muestra solo los turnos del listado.</li>
                            </ul>
                        </InfoOffcanvas>
                        {data.length >= 1 && (
                            <button
                                className="download-btn"
                                onClick={() => exelReports(data, false)}
                                title="Descargar Reporte (hacer clic y esperar)"
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M14 10v2.667A1.333 1.333 0 0112.667 14H3.333A1.333 1.333 0 012 12.667V10M8 10.667L8 2M8 10.667l-2.667-2.666M8 10.667l2.667-2.666"
                                        stroke="currentColor"
                                        strokeWidth="1.3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="btn-text">Excel</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="py-3">

                <InfiniteTable
                    data={data}
                    height={400}
                    columns={[
                        {
                            key: 'createdDate',
                            title: 'Fecha de solicitud',
                            className: 'col-reports-created-date',
                            render: (value: any, row: any) => returnCreation(row)
                        },
                        {
                            key: 'dateTime',
                            title: 'Fecha y hora del turno',
                            className: 'col-reports-date-time',
                            render: (value: any, row: any) => returnDateTime(row)
                        },
                        {
                            key: 'admissionDate',
                            title: 'Fecha y hora de admisión / visita',
                            className: 'col-reports-admission-date',
                            render: (value: any, row: any) => returnAdmissionDate(row)
                        },
                        {
                            key: 'patientName',
                            title: 'Apellido y Nombre',
                            className: 'col-reports-patient-name',
                            render: (value: any, row: any) =>
                                row?.metadata?.patient?.name?.toUpperCase() || row?.patient?.name?.toUpperCase() || row?.patient?.employee?.user?.name?.toUpperCase() || row?.employee?.name?.toUpperCase() || "---"
                        },
                        {
                            key: 'description',
                            title: 'Servicios y prestaciones',
                            className: 'col-reports-description',
                            render: (value: any, row: any) => returnDescription(row)
                        },
                        {
                            key: 'results',
                            title: 'Resultado',
                            className: 'col-reports-results',
                            render: (value: any, row: any) => returnResults(row)
                        },
                        {
                            key: 'actions',
                            title: 'Acciones',
                            className: 'col-reports-actions',
                            render: (value: any, row: any) => actions(row)
                        }
                    ]}
                    loading={loader}
                    hasMore={hasMore}
                    onLoadMore={getAllAppointmentsCall}
                />

                {isActive("modalReporteAusentismo") && (
                    <AbsenteeismResult
                        toggleModal
                        urlTittle={"modalReporteAusentismo"}
                        patient={selectedEmployee}
                        onClose={() => closeModal("modalReporteAusentismo")}
                    />
                )}

                {isActive(VIEW_MULTIPLE_PRACTICES_MODAL) && (
                    <MultiplePracticesModal
                        rowData={rowData}
                        appId={appId}
                        show
                        refreshTable={() => { }}
                        onClose={() => closeModal(VIEW_MULTIPLE_PRACTICES_MODAL)}
                        modalTitle={"Descargar prácticas"}
                        urlTittle={VIEW_MULTIPLE_PRACTICES_MODAL}
                        download={handleOpenPdfReport}
                    />
                )}

                {isActive(VIEW_APPOINTMENT_MODAL) && (
                    <DetailTurnWithDate
                        show
                        modalTitle={"Detalle de turno"}
                        urlTittle={VIEW_APPOINTMENT_MODAL}
                        closeModal={() => closeModal(VIEW_APPOINTMENT_MODAL)}
                    />
                )}

            </div>
        </div>
    )
}

export default Reports